// 스페셜 몬스터 알림 띠(위쪽 가장자리, 플레이 영역을 가리지 않는다): 어두운 바탕 + 금테, 왼쪽 고블린 문양, 가운데 문구, 오른쪽 남은 초.
// 문구·자리 규칙은 specialBandModel.ts. DOM은 한 번만 만들고 바뀔 때만 글자를 고친다(프레임 루프 할당 없음):
// 매 프레임은 SpecialBandGate의 원시값 비교만 하고, 문구 모델·DOM·HUD 상자 측정은 (출현 번호·단계·표시 초·보상·배율·뷰포트)가 바뀌거나 0.5초가 지났을 때만 한다.
// 점멸 없음(섬광 1초 3회 규칙): 띠는 켜지고 꺼질 뿐 깜빡이지 않는다.
import { Scene, type GameView } from '../shared/types';
import { viewH, viewRect, viewW } from '../shared/viewport';
import { C, FONT, el } from './dom';
import { REWARD_BAND_MS, SpecialBandGate, specialBandModel, specialBandPlace, type Box, type SpecialBandModel } from './specialBandModel';

const SVG_NS = 'http://www.w3.org/2000/svg';

export class SpecialBand {
  private readonly root: HTMLDivElement;
  private readonly icon: SVGSVGElement;
  private readonly head: HTMLSpanElement;
  private readonly name: HTMLSpanElement;
  private readonly tip: HTMLSpanElement;
  private readonly tail: HTMLSpanElement;
  private shown = false;
  private lastKey = '';
  private lastPlace = '';
  private rewardSerial = 0;
  private rewardAt = -1e9;
  private model: SpecialBandModel | null = null;
  private readonly gate = new SpecialBandGate();
  /** HUD 상자 측정용 고정 객체(측정마다 새로 만들지 않는다)와 조회한 요소 참조 */
  private readonly pool: { left: number; top: number; right: number; bottom: number }[] = [];
  private poolUsed = 0;
  private readonly obstacles: Box[] = [];
  private elTl: Element | null = null;
  private elTr: Element | null = null;
  private elHint: Element | null = null;
  private obstacleEls: Element[] = [];
  /** 보상 띠를 보이는 시간. ?qa&bandhold(QA 캡처 전용)는 느린 캡처가 띠를 놓치지 않게 길게 둔다 */
  private readonly rewardMs: number;

  constructor(parent: HTMLElement, private readonly view: GameView) {
    const qp = new URLSearchParams(location.search);
    this.rewardMs = qp.has('qa') && qp.has('bandhold') ? 600000 : REWARD_BAND_MS;
    const r = el('div', 'position:absolute;left:50%;top:4px;transform:translateX(-50%);display:none;align-items:center;gap:10px;box-sizing:border-box;width:max-content;max-width:520px;' +
      'height:38px;padding:0 14px 0 10px;border-radius:6px;pointer-events:none;z-index:41;white-space:nowrap;overflow:hidden;' +
      'background:linear-gradient(180deg,rgba(20,26,40,.94),rgba(8,12,24,.94));border:2px solid #b98a2a;' +
      'box-shadow:0 3px 10px rgba(0,0,0,.55),0 0 0 1px rgba(255,224,140,.35) inset,0 0 14px rgba(242,193,78,.3);' +
      'font:800 15px ' + FONT + ';color:#f4ecd8;text-shadow:0 1px 3px rgba(0,0,0,.9);transform-origin:50% 0;', parent);
    r.dataset.testid = 'special-band';
    r.setAttribute('role', 'status');
    r.setAttribute('aria-live', 'polite');
    this.root = r;
    // 고블린 문양(금빛 얼굴 + 뾰족한 귀)
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 32 32');
    svg.style.cssText = 'width:26px;height:26px;flex:0 0 auto;';
    svg.innerHTML = '<path d="M3 9l8 3M29 9l-8 3" stroke="#e8b43a" stroke-width="3.4" stroke-linecap="round"/><ellipse cx="16" cy="17" rx="10" ry="11" fill="#f2c14e" stroke="#9a6a14" stroke-width="1.6"/>' +
      '<circle cx="12" cy="15" r="1.8" fill="#2a1d05"/><circle cx="20" cy="15" r="1.8" fill="#2a1d05"/><path d="M11 22q5 4 10 0" stroke="#2a1d05" stroke-width="1.8" fill="none" stroke-linecap="round"/>';
    r.appendChild(svg);
    this.icon = svg;
    this.head = el('span', 'flex:0 1 auto;overflow:hidden;text-overflow:ellipsis;', r);
    this.name = el('span', 'flex:0 1 auto;font:900 19px ' + FONT + ';color:' + C.gold + ';overflow:hidden;text-overflow:ellipsis;', r);
    this.tip = el('span', 'flex:0 1 auto;font:700 12px ' + FONT + ';color:#cdbf9a;overflow:hidden;text-overflow:ellipsis;', r);
    this.tail = el('span', 'flex:0 0 auto;margin-left:auto;padding-left:12px;font:900 17px ' + FONT + ';color:#fff3c8;font-variant-numeric:tabular-nums;', r);
  }

  private take(): { left: number; top: number; right: number; bottom: number } {
    return this.pool[this.poolUsed++] ?? (this.pool[this.poolUsed - 1] = { left: 0, top: 0, right: 0, bottom: 0 });
  }

  private box(e: Element | null): Box | null {
    if (!e || !e.isConnected) return null;
    const b = viewRect(e);
    const style = getComputedStyle(e);
    if (!(b.width > 0 && b.height > 0) || style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return null;
    const o = this.take();
    o.left = b.left; o.top = b.top; o.right = b.right; o.bottom = b.bottom;
    return o;
  }

  /** 요소 참조 캐시: 없거나 DOM에서 빠졌을 때만 다시 조회한다(측정 때만 호출) */
  private find(cur: Element | null, sel: string): Element | null {
    return cur && cur.isConnected ? cur : document.querySelector(sel);
  }

  update(u: number, nowMs: number): void {
    const v = this.view.special, f = this.view.flow;
    if (v && v.rewardSerial !== this.rewardSerial) { this.rewardSerial = v.rewardSerial; this.rewardAt = nowMs; }
    const inDungeon = f.scene === Scene.Dungeon;
    const rewardOn = !!v && v.rewardSerial > 0 && v.phase === 2 && nowMs - this.rewardAt < this.rewardMs;
    // 문구·보이기: 입력 신호가 바뀔 때만 모델과 글자를 다시 만든다(프레임마다 객체·문자열을 만들지 않는다)
    if (this.gate.contentChanged(v, inDungeon, rewardOn)) {
      const m: SpecialBandModel | null = inDungeon ? specialBandModel(v, nowMs - this.rewardAt, this.rewardMs) : null;
      this.model = m;
      if (!m) { if (this.shown) { this.shown = false; this.root.style.display = 'none'; this.lastKey = ''; this.gate.forgetLayout(); } return; }
      if (!this.shown) { this.shown = true; this.root.style.display = 'flex'; this.lastPlace = ''; this.gate.forgetLayout(); }
      const key = m.tone + '|' + m.head + '|' + m.name + '|' + m.tail + '|' + m.tip;
      if (key !== this.lastKey) {
        this.lastKey = key;
        this.lastPlace = ''; // 문구 길이가 바뀌면(출현 → 처치 보상) 자리를 바로 다시 잡는다
        this.head.textContent = m.head;
        this.name.textContent = m.name;
        this.name.style.display = m.name ? '' : 'none';
        this.tip.textContent = m.tip;
        this.tail.textContent = m.tail;
        this.tail.style.display = m.tail ? '' : 'none';
        this.icon.style.filter = m.tone === 'champion' ? 'hue-rotate(-18deg) saturate(1.2)' : '';
        this.gate.forgetLayout();
      }
    }
    const m = this.model;
    if (!m || !this.shown) return;
    // 자리: 배율·뷰포트가 바뀌었거나 마지막 측정 뒤 0.5초가 지났을 때만 HUD 상자를 잰다(강제 레이아웃 읽기)
    const W = viewW();
    if (!this.gate.layoutDue(u, W, viewH(), nowMs)) return;
    this.poolUsed = 0;
    this.obstacles.length = 0;
    this.elTl = this.find(this.elTl, '[data-testid="hud-top-left"]');
    this.elTr = this.find(this.elTr, '[data-testid="hud-top-right"]');
    this.elHint = this.find(this.elHint, '[data-testid="link-hint"]');
    if (this.obstacleEls.length === 0 || !this.obstacleEls[0].isConnected) {
      this.obstacleEls = Array.from(document.querySelectorAll('[data-testid="hud-objective"],[data-testid="hud-auto"],[data-testid="pause"],[data-testid="fullscreen"],[data-testid^="camera-"],[data-testid="link-stamp"],[data-testid="levelup-band"],[data-testid="skill-learned-band"]'));
    }
    for (const o of this.obstacleEls) {
      const b = this.box(o);
      if (b) this.obstacles.push(b);
    }
    const p = specialBandPlace(W, this.box(this.elTl), this.box(this.elTr), this.box(this.elHint), this.obstacles);
    const pk = p.mode + p.centerX + ',' + p.top + ',' + p.maxWidth + ',' + p.compact + ',' + u.toFixed(3) + m.tone;
    if (pk === this.lastPlace) return;
    this.lastPlace = pk;
    const s = Math.min(1, Math.max(0.62, u));
    const r = this.root;
    r.style.left = p.centerX + 'px';
    r.style.top = p.top + 'px';
    r.style.maxWidth = Math.round(p.maxWidth / s) + 'px';
    r.style.transform = 'translateX(-50%) scale(' + s.toFixed(3) + ')';
    this.head.style.display = p.compact && m.tone === 'gold' ? 'none' : '';
    this.tip.style.display = p.compact || p.maxWidth < 420 ? 'none' : '';
  }
}
